import { getProductByHandle } from '../api/product';
import { getPetitionPledgeCountByExternalProductID } from '../api/storefront/petition';
import { getProductSalesQuantityByExternalProductId } from '../api/storefront/product';

import { ProductStage, ProductTag, ProductType, EvergreenInventoryState, productMOQ } from '../types/common';
import config from '../../config.json';
import { isHolidayHideawayProduct } from './bfcm';

export const parseMetafields = (metafieldsRaw: Shopify.Metafield[] | undefined): Metafields =>
  metafieldsRaw
    ? metafieldsRaw.reduce((acc, edge) => (edge ? { ...acc, [edge.key]: edge.value } : { ...acc, undefined }), {})
    : {};

export const parseProductMetafieldsFromCollection = (metafieldsRaw: Shopify.MetafieldEdge[] | undefined): Metafields =>
  metafieldsRaw
    ? metafieldsRaw.reduce(
        (acc, edge) => (edge ? { ...acc, [edge.node.key]: edge.node.value } : { ...acc, undefined }),
        {},
      )
    : {};

export const parseVariantMetafields = (metafieldsRaw: Shopify.Metafield[] | undefined): VariantMetafields =>
  metafieldsRaw
    ? metafieldsRaw.reduce((acc, edge) => (edge ? { ...acc, [edge.key]: edge.value } : { ...acc, undefined }), {})
    : {};

// The order of the tags is important. The first tag that matches will be the stage of the product.
export const getStage = (tags: string[]): ProductStage => {
  if (tags.includes('draft')) {
    return ProductStage.Draft;
  }
  if (tags.includes('petition')) {
    return ProductStage.Petition;
  }
  if (tags.includes('live')) {
    return ProductStage.Live;
  }
  if (tags.includes('comingsoon')) {
    return ProductStage.ComingSoon;
  }
  if (tags.includes('past')) {
    return ProductStage.Past;
  }
  if (tags.includes('demo')) {
    return ProductStage.Demo;
  }
  if (tags.includes('failed')) {
    return ProductStage.Failed;
  }
  if (tags.includes('successful-petition')) {
    return ProductStage.PetitionSuccess;
  }
  if (tags.includes('failed-petition')) {
    return ProductStage.PetitionFailed;
  }
  return ProductStage.Failed;
};

export const getProductType = (productType: string): ProductType => {
  switch (productType) {
    case 'Plushie':
      return ProductType.Plushie;
    case 'Longboi':
      return ProductType.Longboi;
    case 'Charm':
      return ProductType.Charm;
    case 'Sketchbook':
      return ProductType.Sketchbook;
    case 'Keycap':
      return ProductType.Keycap;
    case 'Backpack':
      return ProductType.Backpack;
    case 'Plush Keychain':
      return ProductType.PlushKeychain;
    case 'Keychain Plushie':
      return ProductType.KeychainPlushie;
    case 'Jumbo Plush':
    case 'Jumbo Plushie':
      return ProductType.JumboPlush;
    case 'Squishie':
      return ProductType.SquishyPlush;
    case 'Vinyl Figure':
      return ProductType.VinylFigure;
    case 'Fanny Pack':
      return ProductType.FannyPack;
    case 'Enamel Pin 1 Pack':
      return ProductType.EnamelPin1Pack;
    case 'Enamel Pin 2 Pack':
      return ProductType.EnamelPin2Pack;
    case 'Enamel Pin 3 Pack':
      return ProductType.EnamelPin3Pack;
    case 'Hoodie':
      return ProductType.Hoodie;
    case 'Zip-Up Hoodie':
      return ProductType.ZipUpHoodie;
    case 'Doughboi':
      return ProductType.Doughboi;
    case 'Knitted Crewneck':
      return ProductType.Crewneck;
    case 'T-Shirt':
      return ProductType.TShirt;
    case 'Sweatpants':
      return ProductType.Sweatpants;
    case 'Cotton Socks':
      return ProductType.CottonSocks;
    case 'Ball Cap':
      return ProductType.BallCap;
    case 'Trucker Hat':
      return ProductType.TruckerHat;
    case 'Sticker':
      return ProductType.Sticker;
    default:
      return ProductType.Plushie;
  }
};

export const getCustomCampaignTime = (tags: string[]): string | undefined => {
  const customCampaignTag = tags.find((tag) => tag.includes('days-'));
  if (customCampaignTag) {
    const customCampaignTime = customCampaignTag.split('days-')[1];
    return customCampaignTime;
  }
  return undefined;
};

export const hasSale = (product: Shopify.Product): boolean =>
  product.variants.edges.find(({ node }) => node.compareAtPrice !== null) !== undefined;

export const getShopifyQuery = (query: string): string => `(title:${query}*)`;

export const getProductVariantsDetails = (productVariantEdge: Shopify.ProductVariantEdge[]) => {
  const hasManyVariants = productVariantEdge.length > 1;
  const priceArray = productVariantEdge
    .map(({ node }) => ({
      price: node.price.amount,
      compareAtPrice: node.compareAtPrice?.amount,
    }))
    .sort((a, b) => a.price - b.price);

  return { hasManyVariants, lowestVariantPrice: priceArray[0] };
};

export const getLiveProductsRemoveCurrentOne = (
  products: Shopify.ProductEdge[],
  currentProduct: Shopify.Product,
): Shopify.ProductEdge[] =>
  products.filter(
    (product) =>
      (product.node.tags.includes(ProductStage.Live) || product.node.tags.includes(ProductStage.Petition)) &&
      !product.node.tags.includes(ProductStage.Draft) &&
      !product.node.tags.includes(ProductTag.Hidden) &&
      product.node.id !== currentProduct.id,
  );

export const filterNonDemoNonDraftProducts = (products: Shopify.ProductEdge[]): Shopify.ProductEdge[] =>
  products.filter((product) => !product.node.tags.includes('demo') && !product.node.tags.includes(ProductStage.Draft));

export const filterTrueMetafieldsCollectionProducts = (
  collections: Shopify.CollectionEdge[],
): Shopify.ProductEdge[] => {
  let filteredProduct: Shopify.ProductEdge[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const collection of collections) {
    if (collection.node.metafield?.value === 'true') {
      filteredProduct = filteredProduct.concat(filterNonDemoNonDraftProducts(collection.node.products.edges));
    }
  }
  return filteredProduct;
};

export const filterTrueMetafieldsCollections = (collections: Shopify.CollectionEdge[]): Shopify.CollectionEdge[] => {
  const filteredCollections: Shopify.CollectionEdge[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const collection of collections) {
    if (collection.node.metafield?.value === 'true') {
      filteredCollections.push(collection);
    }
  }

  return filteredCollections;
};

export const sort360ImageArray = (imageArray: Shopify.Image[]): Shopify.Image[] => {
  const sorted = imageArray.sort(
    (a, b) => a.transformedSrc.split('_360_')[1].split('_')[0] - b.transformedSrc.split('_360_')[1].split('_')[0],
  );
  return sorted;
};

export const hasProductTag = (tags: string[], searchTag: string): boolean =>
  tags.some((tag) => tag.toLowerCase() === searchTag.toLowerCase());

export const containsProductTag = (tags: string[], searchTag: string): boolean =>
  tags.some((tag) => tag.toLowerCase().includes(searchTag.toLowerCase()));

export const productArrayContainsProductTag = (products: Shopify.ProductEdge[], searchTag: string): boolean =>
  products.some((product) => hasProductTag(product.node.tags, searchTag));

// gidString is shopify product.id string like gid://shopify/Product/1562332
export const getProductIDFromShopifyGID = (gidString: string) => {
  if (gidString.includes('Product/')) {
    const gidSplitArray = gidString.split('Product/');
    if (gidSplitArray.length > 1) return gidSplitArray[1];
  }
  // If productID parsing fails due to unexpected data, return original string so data isn't lost
  return gidString;
};

// gidString is shopify product.id string like gid://shopify/ProductVariant/1562332
export const getProductVariantIDFromShopifyGID = (gidString: string) => {
  if (gidString.includes('ProductVariant/')) {
    const gidSplitArray = gidString.split('ProductVariant/');
    if (gidSplitArray.length > 1) return gidSplitArray[1];
  }
  // If productID parsing fails due to unexpected data, return original string so data isn't lost
  return gidString;
};

// gidString is shopify product.id string like gid://shopify/Cart/a3928c9d9f93d6f05acb502932f15598?key=4841bc1feac971d8f135e15fd3849e87
export const getCartTokenFromShopifyGID = (gidString: string) => {
  if (gidString.includes('Cart/')) {
    const gidSplitArray = gidString.split('Cart/');
    if (gidSplitArray.length > 1) {
      const cartTokenArray = gidSplitArray[1].split('?');

      if (gidSplitArray.length > 0) return cartTokenArray[0];

      return gidSplitArray[1];
    }
  }
  // If cartID parsing fails due to unexpected data, return original string so data isn't lost
  return gidString;
};

// Creates Subscription Tags from campaign stage
export const getCampaignStageTag = (
  stage: string,
  totalInventory: number,
  moq: number,
  isSoldOut?: boolean,
): string => {
  switch (stage) {
    case ProductStage.Failed:
      return `completed-not-funded`;
    case ProductStage.Past:
      return `completed-funded`;
    case ProductStage.Live:
      if (isSoldOut) return `active-sold-out`;
      return totalInventory >= moq ? `active-funded` : `active-not-funded`;
    case ProductStage.ComingSoon:
      return `coming-soon`;
    case ProductStage.Petition:
    case ProductStage.PetitionSuccess:
    case ProductStage.PetitionFailed:
      return `petition`;
    default:
      return '';
  }
};

export const isStagingEnv = () => process.env.NEXT_PUBLIC_DOMAIN && process.env.NEXT_PUBLIC_DOMAIN.includes('staging');

export const updateFundedBar = (
  stage: ProductStage,
  product: Shopify.Product,
  totalInventory: number,
  setTotalInventory: (totalInventory: number) => void,
) => {
  const updateInventory = setInterval(
    async () => {
      if (stage !== ProductStage.Live) return;

      const isLimited = hasProductTag(product.tags, ProductTag.Limited);
      const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
      const hasFundedBarOverride = hasProductTag(product.tags, ProductTag.FundedBarOverride);

      let newProduct = product;

      if (isLimited || isEvergreen || hasFundedBarOverride) {
        newProduct = (await Promise.resolve(getProductByHandle(product.handle))) ?? product;
      }

      if (!newProduct) return;

      const newTotalInventory = await calculateTotalInventorySold(newProduct);

      if (newTotalInventory === null) return;

      if (newTotalInventory !== totalInventory) {
        setTotalInventory(newTotalInventory);
      }
    },
    10_000, // Every 10 seconds
    10_000, // Start after 10 seconds upon load
  );

  return () => clearInterval(updateInventory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
};

export const updatePetitionFundedBar = (
  stage: ProductStage,
  product: Shopify.Product,
  totalInventory: number,
  setTotalInventory: (totalInventory: number) => void,
) => {
  const updateInventory = setInterval(
    async () => {
      if (stage !== ProductStage.Petition) return;

      const pledges = await Promise.resolve(
        getPetitionPledgeCountByExternalProductID(getProductIDFromShopifyGID(product.id)),
      )
        .then((response) => response.data)
        .catch(() => totalInventory);

      const newTotalInventory = Math.abs(pledges);

      if (newTotalInventory !== totalInventory) {
        setTotalInventory(newTotalInventory);
      }
    },
    10_000, // Every 10 seconds
    10_000, // Start after 10 seconds upon load
  );

  return () => clearInterval(updateInventory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
};

export const calculateTotalInventorySold = async (product: Shopify.Product) => {
  const isLimited = hasProductTag(product.tags, ProductTag.Limited);
  const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
  const stage = getStage(product.tags);
  const metafields = parseMetafields(product.metafields);

  if (isLimited && (stage === ProductStage.Past || (product.totalInventory ?? 0) <= 0)) {
    return 0;
  }

  if (isLimited || isEvergreen) {
    return product.totalInventory ?? 0;
  }

  if (stage === ProductStage.ComingSoon) {
    return 0;
  }

  if (stage === ProductStage.Past && metafields.totalsold) {
    return metafields.totalsold;
  }

  const hasFundedBarOverride = hasProductTag(product.tags, ProductTag.FundedBarOverride);

  let productSalesQuantity: ProductSalesQuantity | null;

  if (!hasFundedBarOverride) {
    productSalesQuantity = await getProductSalesQuantityByExternalProductId(getProductIDFromShopifyGID(product.id))
      .then((response) => response.data)
      .catch(() => null);
  } else {
    const shopifyProductInventory = Math.abs(product.totalInventory ?? 0);
    productSalesQuantity = {
      quantity: shopifyProductInventory,
    };
  }

  const currentProductSalesQuantity = productSalesQuantity ? productSalesQuantity.quantity : null;

  const totalInventory = currentProductSalesQuantity;

  return totalInventory;
};

export const isProductSoldOut = (product: Shopify.Product, totalInventory: number, stage?: ProductStage): boolean =>
  stage === ProductStage.Past ||
  ((hasProductTag(product.tags, ProductTag.Limited) || hasProductTag(product.tags, ProductTag.Evergreen)) &&
    totalInventory <= 0);

export const shouldShowLowStockMessage = (lowStockThreshold: number | undefined, currentInventory: number): boolean => {
  if (!lowStockThreshold || currentInventory <= 0) return false;
  const threshold = Number(lowStockThreshold);
  return threshold >= currentInventory;
};

export const getEvergreenInventoryState = (product: Shopify.Product) => {
  if (!product.tags.includes(ProductTag.Evergreen)) return undefined;
  if (!product.totalInventory || product.totalInventory <= 0) return EvergreenInventoryState.SoldOut;
  if (product.totalInventory <= 50) return EvergreenInventoryState.LowStock;
  return EvergreenInventoryState.InStock;
};

export const shouldShowProgressBar = (stage: ProductStage, productID: string, isEvergreen?: boolean) =>
  !isEvergreen &&
  stage !== ProductStage.ComingSoon &&
  stage !== ProductStage.Failed &&
  !shouldHideProgressBarForProducts(productID);

// Assumes gift card title is formatted as "Gift a X"
export const parseGiftCardProductName = (giftCardTitle: string) => giftCardTitle.split('Gift a ')[1];

export const renderRedirectText = (
  creator: string | undefined,
  productsBySameCreatorToDisplay: Shopify.ProductEdge[],
  productsInSameMakeshiftCollectionToDisplay: Shopify.ProductEdge[],
  makeshiftCollectionTitle: string | null | undefined,
) => {
  if (creator && productsBySameCreatorToDisplay.length === 1) {
    if (productArrayContainsProductTag(productsBySameCreatorToDisplay, ProductStage.Petition)) {
      return `Support ${creator}'s newest petition! 🚀`;
    }
    return `${creator} just dropped a new product. Grab it before it's gone! ✨`;
  }
  if (creator && productsBySameCreatorToDisplay.length > 1) {
    return `Complete your ${creator} Collection! ✨`;
  }
  if (makeshiftCollectionTitle && productsInSameMakeshiftCollectionToDisplay.length === 1) {
    return `${makeshiftCollectionTitle} just dropped a new product. Grab it before it's gone! ✨`;
  }
  if (makeshiftCollectionTitle && productsInSameMakeshiftCollectionToDisplay.length > 1) {
    return `Complete your ${makeshiftCollectionTitle} Collection! ✨`;
  }
  return `Best sellers we think you'll like:`;
};

export const renderLink = (
  productsBySameCreatorToDisplay: Shopify.ProductEdge[],
  productsInSameMakeshiftCollectionToDisplay: Shopify.ProductEdge[],
) => {
  if (productsBySameCreatorToDisplay.length > 0) {
    return `${productsBySameCreatorToDisplay[0].node.handle}`;
  }
  if (productsInSameMakeshiftCollectionToDisplay.length > 0) {
    return `${productsInSameMakeshiftCollectionToDisplay[0].node.handle}`;
  }
  return `/shop/featured`;
};

export const getProductsDisplayed = (
  productsBySameCreator: Shopify.ProductEdge[],
  productsInSameMakeshiftCollection: Shopify.ProductEdge[],
  topCampaigns: Shopify.ProductEdge[],
) => {
  const MAX_PRODUCTS_TO_DISPLAY = 3;
  if (productsBySameCreator.length > 0) {
    return productsBySameCreator.map((product) => getProductIDFromShopifyGID(product.node.id));
  }
  if (productsInSameMakeshiftCollection.length > 0) {
    return productsInSameMakeshiftCollection.map((product) => getProductIDFromShopifyGID(product.node.id));
  }
  return topCampaigns.slice(0, MAX_PRODUCTS_TO_DISPLAY).map((product) => getProductIDFromShopifyGID(product.node.id));
};

export const getFirstThreeUniqueCreators = (topCampaigns: Shopify.ProductEdge[]): Shopify.ProductEdge[] => {
  const uniqueCreators = new Set();
  const uniqueCampaigns: Shopify.ProductEdge[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const campaign of topCampaigns) {
    const { creator } = parseMetafields(campaign.node.metafields);

    if (!uniqueCreators.has(creator)) {
      uniqueCreators.add(creator);
      uniqueCampaigns.push(campaign);
    }

    if (uniqueCampaigns.length === 3) {
      return uniqueCampaigns;
    }
  }

  return uniqueCampaigns;
};

export const isEligibleForTimer = (product: Shopify.Product) => {
  if (hasProductTag(product.tags, ProductTag.Limited)) {
    return hasProductTag(product.tags, ProductStage.ComingSoon);
  }

  return !hasProductTag(product.tags, ProductTag.Evergreen);
};

export const findCollectionByMetafield = (
  product: Shopify.Product,
  key: string,
  value: string,
): Shopify.CollectionEdge | undefined =>
  product.collections.edges.find((collection) =>
    collection.node.metafields?.some((metafield) => metafield?.key === key && metafield?.value === value),
  );

const models: Record<string, string> = {
  'gid://shopify/Product/8274581913756': `Graeme is 6'2"/188cm & wears a size XL. Alicia is 5'7"/170cm & wears a size S.`,
  'gid://shopify/Product/8318852759708': `Madison is 5'9"/175cm & wears a size XL. Nary is 5'1"/155cm & wears a size S.`,
  'gid://shopify/Product/8284085125276': `Model wears a size XS.`,
  'gid://shopify/Product/8328894742684': `Model wears a size XS.`,
};

export const getModelsMeasurements = (id: string): string | undefined => models[id] ?? null;

export const getProductMOQ = (product: Shopify.Product) => {
  const metafields = parseMetafields(product.metafields);
  return Number(metafields.moq) || Number(productMOQ[product.productType]) || Number(config.defaultMOQ);
};

export const getProgressBarProps = (product: Shopify.Product, isOnProductCard: boolean, totalSold: number) => {
  const stage = getStage(product.tags);
  const isLimited = hasProductTag(product.tags, ProductTag.Limited);
  const moq = getProductMOQ(product);

  const isGradient = isLimited;
  const height = isOnProductCard ? 4 : 8;
  const funded = isLimited ? totalSold <= 0 : totalSold >= moq;

  const percentage = (() => {
    if (isLimited) {
      if (stage === ProductStage.Past || totalSold <= 0) {
        return moq;
      }
      return (moq - totalSold) / moq;
    }
    return totalSold / moq;
  })();

  return {
    isGradient,
    percentage,
    funded,
    height,
  };
};

export const isApparelProduct = (product: Shopify.Product | undefined): boolean => {
  if (!product) return false;
  const productType = getProductType(product.productType);
  return (
    productType === ProductType.Hoodie ||
    productType === ProductType.ZipUpHoodie ||
    productType === ProductType.Crewneck ||
    productType === ProductType.TShirt ||
    productType === ProductType.Sweatpants ||
    productType === ProductType.CottonSocks ||
    productType === ProductType.BallCap ||
    productType === ProductType.TruckerHat
  );
};

export const isSizedApparelProduct = (product: Shopify.Product | undefined): boolean => {
  if (!product) return false;
  return isApparelProduct(product) && !isOneSizeApparelProduct(product);
};

export const isOneSizeApparelProduct = (product: Shopify.Product | undefined): boolean => {
  if (!product) return false;
  const productType = getProductType(product.productType);
  return productType === ProductType.BallCap || productType === ProductType.TruckerHat;
};

// Assumes the given date is from Shopify Date Metafield
export const isCurrentDateBeforeGivenMetafieldDate = (date: string | undefined): boolean => {
  if (date === undefined) return false;
  return new Date().getTime() < new Date(date).getTime();
};

export const getVariantShipDateInfo = (product: Shopify.Product) => {
  const variantShipDates = product.variants.edges
    .map(({ node }) => parseVariantMetafields(node.metafields).shipByDate)
    .filter(Boolean) as string[];

  const hasMultipleVariants = product.variants.edges.length > 1;
  const hasVariantShipDates = variantShipDates.length > 0;

  // Check if variants have different ship dates
  const hasDifferentShipDates =
    hasMultipleVariants && hasVariantShipDates && !variantShipDates.every((date) => date === variantShipDates[0]);

  // Check if all variants are ready-to-ship
  const allVariantsReadyToShip =
    hasMultipleVariants &&
    hasVariantShipDates &&
    variantShipDates.every((date) => !isCurrentDateBeforeGivenMetafieldDate(date));

  // Check for mix of ready-to-ship and pre-order variants
  const variantShipTypeMix = product.variants.edges.reduce(
    (acc, edge) => {
      const { shipByDate } = parseVariantMetafields(edge.node.metafields);

      if (shipByDate) {
        if (isCurrentDateBeforeGivenMetafieldDate(shipByDate)) {
          return { ...acc, hasPreOrder: true };
        }
        return { ...acc, hasReadyToShip: true };
      }

      return acc;
    },
    { hasReadyToShip: false, hasPreOrder: false },
  );

  return {
    variantShipDates,
    hasMultipleVariants,
    hasVariantShipDates,
    hasDifferentShipDates,
    allVariantsReadyToShip,
    hasReadyToShip: variantShipTypeMix.hasReadyToShip,
    hasPreOrder: variantShipTypeMix.hasPreOrder,
  };
};

export const getEffectiveShipDate = (
  product: Shopify.Product,
  selectedVariant?: Shopify.ProductVariant,
  productDeliveryDate?: string | null,
): string | null | undefined => {
  const { variantShipDates, hasVariantShipDates, hasDifferentShipDates } = getVariantShipDateInfo(product);

  // If a specific variant is selected, use its ship date first
  if (selectedVariant) {
    const variantDate = parseVariantMetafields(selectedVariant.metafields).shipByDate;
    if (variantDate) return variantDate;
  }

  // If all variants have the same ship date, use it
  if (hasVariantShipDates && !hasDifferentShipDates) {
    return variantShipDates[0];
  }

  // Fall back to product-level delivery date
  return productDeliveryDate;
};

export const calculateCombinedMOQ = (collection: Shopify.Collection | null): number | null => {
  if (!collection) return null;

  return collection.products.edges.reduce((acc, productEdge: Shopify.ProductEdge) => {
    const product = productEdge.node;
    return acc + getProductMOQ(product);
  }, 0);
};

export const shouldHideProgressBarForProducts = (productID: string): boolean => {
  // Hazem Plush
  const productsIDsToHide = ['8318624235676'];
  return productsIDsToHide.includes(getProductIDFromShopifyGID(productID)) || isHolidayHideawayProduct(productID);
};

export const autoRefreshPage = (
  product: Shopify.Product,
  stage: ProductStage,
  targetDate: string | undefined,
  setIsTransitioning: (isTransitioning: boolean) => void,
  setHasTransitioned: (hasTransitioned: boolean) => void,
) => {
  const refreshPage = setInterval(
    async () => {
      if (stage !== ProductStage.ComingSoon && stage !== ProductStage.Live) return;
      if (!targetDate) return;

      // calculate time left
      const timeLeft = new Date(targetDate).getTime() - new Date().getTime();

      if (timeLeft <= 0) {
        setIsTransitioning(true);
        const productTags = await getProductByHandle(product.handle)
          .then((response) => response?.tags ?? [])
          .catch(() => {
            setHasTransitioned(true);
            return [];
          });
        if (stage === ProductStage.ComingSoon && hasProductTag(productTags, ProductTag.Live)) {
          setHasTransitioned(true);
        } else if (
          stage === ProductStage.Live &&
          (hasProductTag(productTags, ProductTag.Past) || hasProductTag(productTags, ProductTag.Failed))
        ) {
          setHasTransitioned(true);
        }
      }
    },
    5_000, // Every 5 seconds
    5_000, // Start after 5 seconds upon load
  );

  return () => clearInterval(refreshPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
};
